import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AsyncReduxState, initialAsyncReduxState } from '../../../redux/@config/AsyncReduxState'
import { DocumentViewModel, UserDocumentViewModel } from '../../../models'

export type FilesState = AsyncReduxState<{
    // PZ documents list
    documents: DocumentViewModel[];
    // User-specific documents (management page)
    userDocuments: UserDocumentViewModel[];
    // Loading states for individual files
    loadingFileIds: string[];
    // Pagination
    amountOfPages: number;
}>;

const initialState: FilesState = {
    ...initialAsyncReduxState,
    documents: [],
    userDocuments: [],
    loadingFileIds: [],
    amountOfPages: 1,
}

export const FilesSlice = createSlice({
    name: 'filesReducer',
    initialState,
    reducers: {
        setIsLoading(state, action: PayloadAction<boolean>): FilesState {
            return {
                ...state,
                isLoading: action.payload,
            }
        },
        setError(state, action: PayloadAction<string>): FilesState {
            return {
                ...state,
                error: action.payload,
            }
        },
        setIsSuccessful(state, action: PayloadAction<boolean>): FilesState {
            return {
                ...state,
                isSuccessful: action.payload,
            }
        },
        // PZ Documents
        setDocuments(state, action: PayloadAction<DocumentViewModel[]>): FilesState {
            return {
                ...state,
                documents: action.payload,
            }
        },
        setAmountOfPages(state, action: PayloadAction<number>): FilesState {
            return {
                ...state,
                amountOfPages: action.payload,
            }
        },
        documentAdded(state, action: PayloadAction<DocumentViewModel>): FilesState {
            return {
                ...state,
                documents: [...state.documents, action.payload],
            }
        },
        documentPatched(state, action: PayloadAction<DocumentViewModel>): FilesState {
            return {
                ...state,
                documents: state.documents.map(doc =>
                    (doc.id === action.payload.id ? action.payload : doc)),
            }
        },
        documentDeleted(state, action: PayloadAction<string>): FilesState {
            return {
                ...state,
                documents: state.documents.filter(doc => doc.id !== action.payload),
            }
        },
        // User Documents (management)
        setUserDocuments(state, action: PayloadAction<UserDocumentViewModel[]>): FilesState {
            return {
                ...state,
                userDocuments: action.payload,
            }
        },
        userDocumentAdded(state, action: PayloadAction<UserDocumentViewModel>): FilesState {
            return {
                ...state,
                userDocuments: [...state.userDocuments, action.payload],
            }
        },
        userDocumentPatched(state, action: PayloadAction<UserDocumentViewModel>): FilesState {
            return {
                ...state,
                userDocuments: state.userDocuments.map(doc =>
                    (doc.id === action.payload.id ? action.payload : doc)),
            }
        },
        userDocumentDeleted(state, action: PayloadAction<string>): FilesState {
            return {
                ...state,
                userDocuments: state.userDocuments.filter(doc => doc.id !== action.payload),
            }
        },
        // Loading states for individual files
        setLoadingFile(state, action: PayloadAction<string>): FilesState {
            return {
                ...state,
                loadingFileIds: [...state.loadingFileIds, action.payload],
            }
        },
        removeLoadingFile(state, action: PayloadAction<string>): FilesState {
            return {
                ...state,
                loadingFileIds: state.loadingFileIds.filter(id => id !== action.payload),
            }
        },
        // Reset state
        resetFilesState(): FilesState {
            return initialState
        },
    },
})

export const {
    setIsLoading,
    setError,
    setIsSuccessful,
    setDocuments,
    setAmountOfPages,
    documentAdded,
    documentPatched,
    documentDeleted,
    setUserDocuments,
    userDocumentAdded,
    userDocumentPatched,
    userDocumentDeleted,
    setLoadingFile,
    removeLoadingFile,
    resetFilesState,
} = FilesSlice.actions

export default FilesSlice.reducer
